import { goto } from '$app/navigation';
import {
	AOFL_PRODUCT_NAMES,
	LOCAL_STORAGE_KEYS,
	PAGES,
	QUERY_PARAM_KEYS,
} from '$lib/utils/constants';
import { LOGIN_ERROR_TYPES, LOGIN_URL_QUERY_KEYS } from '../../../routes/login/__config/common';
import { redirect } from '@sveltejs/kit';
import { base } from '$app/paths';
import { Sentry } from '$lib/services/sentry';
import { Analytics } from '$lib/services/analytics/client';
import { EVENT_TYPES } from '$events';
import { resetStore, user } from '$lib/stores/user/store';
import { app } from '$lib/stores/app/store';
import { get } from 'svelte/store';
import { has2_0Account } from '$lib/utils/aofl-accounts.js';
import { logout } from '$lib/stores/user/action.js';
import {
	Authorizer as PackageAuthorizer,
	clearVisitedPaths,
	getVisitedPaths,
} from '@mkt-client/route-authorizer';

export { clearVisitedPaths };

const navigation = {
	async navigate(url, options = { replaceState: false }) {
		if (typeof window === 'undefined') {
			throw redirect(301, url);
		}
		if (url.startsWith('http')) {
			window.location.href = url;
			return;
		}
		await goto(url, options);
	},
};

const hooks = {
	onAccessDenied: defaultPolicyAuthorizer,
	onBeforeRedirect(pathToRedirect) {
		Sentry.addBreadcrumb({
			category: 'navigation',
			message: `Authorizer redirecting to ${pathToRedirect}`,
			level: 'info',
		});
	},
	onCycleDetected: async (context) => {
		logRedirectLoop(context);
		await logout(get(app), { redirect: false });
		return (
			PAGES.INTERNAL.LOGIN + `?${QUERY_PARAM_KEYS.ERROR_TYPE}=${LOGIN_ERROR_TYPES.REDIRECT_LOOP}`
		);
	},
	onInvalidInput(message) {
		if (import.meta.env.DEV) {
			console.error(message);
		} else {
			Sentry.addBreadcrumb({
				category: 'authorizer-validate',
				message,
				level: 'info',
			});
		}
	},
	onRedirectLoop: logRedirectLoop,
};

const authorizerConfig = {
	basePath: base,
	hooks,
	navigation,
	shouldReplaceStateOnRedirect: (url) => url.startsWith(PAGES.INTERNAL.CREATE_ACCOUNT),
};

/**
 * Redirect to login with the current path as the return destination.
 * Used when the authenticated app handoff cannot proceed due to a missing or
 * failed short-lived token.
 * @returns {Promise<void>}
 */
export async function redirectToLoginOnTokenFailure() {
	const destination = `${window.location.pathname}${window.location.search}${window.location.hash}`;
	const params = new URLSearchParams();
	params.set(QUERY_PARAM_KEYS.ERROR_TYPE, LOGIN_ERROR_TYPES.TOKEN_EXPIRED);
	params.set(LOGIN_URL_QUERY_KEYS.REDIRECT_TO, destination);
	await goto(`${PAGES.INTERNAL.LOGIN}?${params.toString()}`);
}

let crossTabLogoutListenerInitialized = false;

/**
 * Registers a storage event listener to detect cross-tab logout.
 * When another tab clears the AOFL token, redirect current tab to login.
 * Safe to call multiple times — only one listener is ever registered per page lifetime.
 * @returns {void}
 */
export function initCrossTabLogoutListener() {
	if (typeof window === 'undefined') return;
	if (crossTabLogoutListenerInitialized) return;
	crossTabLogoutListenerInitialized = true;

	window.addEventListener('storage', (event) => {
		if (event.key === LOCAL_STORAGE_KEYS.AOFL_TOKEN && event.newValue === null) {
			resetStore();
			goto(PAGES.INTERNAL.LOGIN);
		}
	});
}

/**
 * Logs redirect loop telemetry to Sentry.
 * @param {import('@mkt-client/route-authorizer').RedirectLoopContext} context - Redirect loop details from the package authorizer.
 */
function logRedirectLoop(context) {
	const currentUser = get(user);
	const productName = has2_0Account()
		? AOFL_PRODUCT_NAMES.ABCMOUSE_2_0
		: AOFL_PRODUCT_NAMES.ABCMOUSE;

	Sentry.captureException(new Error('Redirect cycle detected'), {
		tags: {
			issue: LOGIN_ERROR_TYPES.REDIRECT_LOOP,
			subscription_path: currentUser?.userMetaData?.subscription_path,
			completed_step: currentUser?.userMetaData?.completed_step,
			productName,
		},
		extra: {
			hasCycle: context.hasCycle,
			pathToRedirect: context.pathToRedirect,
			currentPath: context.currentPath,
			visitedPaths: context.visitedPaths.length ? context.visitedPaths : [...getVisitedPaths()],
		},
	});
}

/**
 * @returns {Promise<string>} Default deny redirect path when no policy exception handler is set.
 */
async function defaultPolicyAuthorizer() {
	await Analytics.log(EVENT_TYPES.CLIENT_ACCESS_DENIED, {
		policy: 'DEFAULT_POLICY_AUTHORIZER',
		reason: 'no_matching_policy',
		destination: PAGES.INTERNAL.HOME,
	});
	return PAGES.INTERNAL.HOME;
}

export default class Authorizer {
	/**
	 * @param {import('@mkt-client/route-authorizer').AuthorizerProps} props - Route path and policy pair for this authorization check.
	 */
	constructor(props) {
		this.#inner = new PackageAuthorizer(props, authorizerConfig);
	}

	/** @type {PackageAuthorizer} */
	#inner;

	/**
	 * @param {import('@mkt-client/route-authorizer').AuthorizerProps} props - Route path and policy pair to validate.
	 * @returns {boolean} Whether the props satisfy the authorizer schema.
	 */
	validate(props) {
		return this.#inner.validate(props);
	}

	async browserAuthorize() {
		return this.#inner.browserAuthorize();
	}

	static async defaultPolicyAuthorizer() {
		return defaultPolicyAuthorizer();
	}

	static async redirect(url, options = { replaceState: false }) {
		return navigation.navigate(url, options);
	}
}
